import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../lib/cn.js'
import { Icon } from './Icon.js'
import { useOverlay } from './overlay.js'

/**
 * A modal for short-lived overlays that should not replace the page underneath.
 *
 * Used for the job card on the dispatch board (so the board position is not
 * lost) and the user edit popup on Settings → Users (so the list stays put
 * while the full ServiceM8 staff record is reviewed).
 *
 * On a phone the job card presents as a `Sheet` instead. Escape handling, the
 * scroll lock and the stacking order are shared between the two through
 * `useOverlay`.
 *
 * The scrim is ink-900 at 40%, one of only two places transparency appears.
 */

export function Modal({
  open,
  onClose,
  labelledBy,
  className,
  children,
}: {
  open: boolean
  onClose: () => void
  labelledBy?: string
  className?: string
  children: ReactNode
}) {
  const panel = useRef<HTMLDivElement>(null)
  const depth = useOverlay(open, onClose)

  // Move focus in so Escape and Tab land somewhere sensible.
  useEffect(() => {
    if (open) panel.current?.focus()
  }, [open])

  if (!open) return null

  return createPortal(
    <div
      className="fixed inset-0 flex items-end justify-center bg-ink-900/40 sm:items-center sm:p-4"
      style={{ zIndex: 50 + depth * 10 }}
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        className={cn(
          // A phone gets the dialog as a sheet off the bottom edge, where the
          // thumb already is; a desk gets it centred.
          'flex max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-2xl bg-white pb-[var(--safe-bottom)] shadow-xl outline-none sm:max-h-full sm:rounded-2xl sm:pb-0',
          className,
        )}
      >
        {children}
      </div>
    </div>,
    document.body,
  )
}

export function ModalCloseButton({ onClose }: { onClose: () => void }) {
  return (
    <button
      type="button"
      onClick={onClose}
      aria-label="Close"
      className="grid size-8 shrink-0 place-items-center rounded-md text-ink-500 transition-colors hover:bg-ink-100 hover:text-ink-900"
    >
      <Icon name="close" />
    </button>
  )
}

/**
 * The top of a dialog: what it is, a line on what it does, and the close
 * button. Every form dialog uses this so they read as one family.
 */
export function DialogHeader({
  id,
  title,
  description,
  onClose,
}: {
  id: string
  title: ReactNode
  description?: ReactNode
  onClose: () => void
}) {
  return (
    <div className="flex items-start gap-3 border-b border-ink-100 px-4 py-3">
      <div className="min-w-0 flex-1">
        <h2 id={id} className="font-display text-[17px] leading-snug font-bold text-brand-800">
          {title}
        </h2>
        {description ? <p className="mt-0.5 text-xs text-ink-500">{description}</p> : null}
      </div>
      <ModalCloseButton onClose={onClose} />
    </div>
  )
}

/** The scrolling middle of a dialog. */
export function DialogBody({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div className={cn('flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 py-3', className)}>
      {children}
    </div>
  )
}

/**
 * The bottom of a dialog: an error or a hint on the left, the actions on the
 * right. It stays put while the body scrolls, so the save button is never
 * below the fold on a phone.
 */
export function DialogFooter({
  error,
  hint,
  children,
}: {
  error?: string | null
  hint?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-2 border-t border-ink-100 bg-ink-50/60 px-4 py-2.5">
      {error ? (
        <p className="mr-auto text-xs text-red-600" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="mr-auto text-xs text-ink-500">{hint}</p>
      ) : null}
      {children}
    </div>
  )
}
